import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const TraumaOutreach = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [symptomsCanScrollLeft, setSymptomsCanScrollLeft] = useState(false);
  const [symptomsCanScrollRight, setSymptomsCanScrollRight] = useState(true);
  const [causesCanScrollLeft, setCausesCanScrollLeft] = useState(false);
  const [causesCanScrollRight, setCausesCanScrollRight] = useState(true);
  const [typesCanScrollLeft, setTypesCanScrollLeft] = useState(false);
  const [typesCanScrollRight, setTypesCanScrollRight] = useState(true);
  const symptomsScrollRef = useRef(null);
  const causesScrollRef = useRef(null);
  const typesScrollRef = useRef(null);
  const treatmentScrollRef = useRef(null);

  // Scroll detection for active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'symptoms', 'causes', 'types', 'treatment'];
      const scrollPosition = window.scrollY + 250; // Offset for better detection

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filterContent = (section) => {
    setActiveSection(section);
  };

  const checkScrollPosition = (ref) => {
    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;
      const canScrollLeft = scrollLeft > 0;
      const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1;
      return { canScrollLeft, canScrollRight };
    }
    return { canScrollLeft: false, canScrollRight: false };
  };

  const scrollSymptoms = (direction) => {
    if (symptomsScrollRef.current) {
      const scrollAmount = 320; // Card width + gap
      symptomsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(symptomsScrollRef);
        setSymptomsCanScrollLeft(position.canScrollLeft);
        setSymptomsCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  const scrollCauses = (direction) => {
    if (causesScrollRef.current) {
      const scrollAmount = 320;
      causesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(causesScrollRef);
        setCausesCanScrollLeft(position.canScrollLeft);
        setCausesCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  const scrollTypes = (direction) => {
    if (typesScrollRef.current) {
      const scrollAmount = 320;
      typesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(() => {
        const position = checkScrollPosition(typesScrollRef);
        setTypesCanScrollLeft(position.canScrollLeft);
        setTypesCanScrollRight(position.canScrollRight);
      }, 300);
    }
  };

  const scrollTreatment = (direction) => {
    if (treatmentScrollRef.current) {
      const scrollAmount = 320;
      treatmentScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[#E6F7F4]"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
          <div className="relative overflow-hidden rounded-3xl bg-[#E6F7F4]">
            <div className="relative px-6 py-24 sm:px-10 sm:py-32 lg:px-12 lg:py-36 text-center">
              <img
                src="/static/img/services/trauma.png"
                alt=""
                className="pointer-events-none absolute -right-2 top-1/2 hidden w-80 h-80 -translate-y-1/2 opacity-75 lg:block"
              />

              <div className="mx-auto flex items-center justify-center gap-2 text-xs tracking-wide text-gray-700">
                <span>Child Services</span>
              </div>

              <h1 className="mt-6 font-extrabold tracking-tight text-gray-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.05]">
                Trauma <br/>Outreach
              </h1>

              <p className="mt-6 text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Specialized support for trauma recovery and healing. Evidence-based <br/>interventions, trauma-informed care, and compassionate therapy for children<br/>  affected by traumatic experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Action Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center py-4">
            <div className="flex flex-wrap items-center gap-6">
              <a 
                href="#overview" 
                className={`font-medium transition-colors ${
                  activeSection === 'overview' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('overview');
                  const offset = 200; // Height of main navbar + action bar + extra spacing + padding
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Overview
              </a>
              <a 
                href="#symptoms" 
                className={`font-medium transition-colors ${
                  activeSection === 'symptoms' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('symptoms');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Symptoms
              </a>
              <a 
                href="#causes" 
                className={`font-medium transition-colors ${
                  activeSection === 'causes' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('causes');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Benefits
              </a>
              <a 
                href="#types" 
                className={`font-medium transition-colors ${
                  activeSection === 'types' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('types');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }}
              >
                Services
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Overview */}
          <div id="overview" className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Overview</h2>
            <p className="text-lg text-gray-600 mb-8">
              Outreach to families affected by trauma is a vital component of trauma-informed care and support. 
              Trauma can have profound and long-lasting effects on individuals and their families, and outreach efforts aim to connect these families with appropriate resources, information, and assistance. 
              The goal is to help families cope with trauma, build resilience, and access the services they need to heal and recover.
            </p>
            <p className="text-lg text-gray-600 mb-8">
              A traumatic event is one that threatens injury, death, or the physical integrity of self or others and also causes horror, terror, or helplessness at the time it occurs. 
              Traumatic events include sexual abuse, physical abuse, domestic violence, community and school violence, medical trauma, motor vehicle accidents, acts of terrorism, war experiences, natural and human-made disasters, suicides, and other traumatic losses. 
            </p>
          </div>
        </div>

        {/* Symptoms Section with Full-Width Background */}
        <div id="symptoms" className="py-16 bg-[#00A785]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Trauma Symptoms</h2>
            
            {/* Common Signs */}
            <div>
              <h3 className="text-lg font-normal text-white mb-6">Recognizing signs that may indicate trauma in children and teens</h3>
              
              {/* Scroll Container */}
              <div 
                ref={symptomsScrollRef}
                className="overflow-x-auto scrollbar-hide mb-4"
              >
                <div className="flex space-x-4 py-4">
                  {[
                    {
                      symptom: "Mood Swings",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Behavioral Issues",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Memory Problems",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Trust Issues",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Low Self-Worth",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Physical Pain",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Always On Edge",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Repeating Trauma",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )
                    }
                  ].map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-[300px]">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {item.icon}
                        </div>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 mb-2">{item.symptom}</h4>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-center items-center space-x-4">
                <button
                  onClick={() => scrollSymptoms('left')}
                  disabled={!symptomsCanScrollLeft}
                  className={`border border-gray-200 rounded-lg p-3 shadow-lg hover:shadow-xl transition-shadow ${
                    symptomsCanScrollLeft 
                      ? 'bg-white text-gray-600 hover:bg-gray-50' 
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={() => scrollSymptoms('right')}
                  disabled={!symptomsCanScrollRight}
                  className={`border border-gray-200 rounded-lg p-3 shadow-lg hover:shadow-xl transition-shadow ${
                    symptomsCanScrollRight 
                      ? 'bg-white text-gray-600 hover:bg-gray-50' 
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Hide Scrollbar Styles */}
        <style jsx>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>

      {/* Benefits Section with Full-Width Background */}
      <div id="causes" className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Benefits of Trauma Outreach</h2>
          
          {/* Key Benefits */}
          <div>
            <h3 className="text-lg font-normal text-gray-700 mb-6">How trauma outreach supports healing and recovery</h3>
            
            {/* Grid Container */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: "Better Access to Help",
                  description: "Helps connect families with important services such as counseling, support groups, legal services, housing, and medical services.",
                  icon: (
                    <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  )
                },
                {
                  title: "Stronger Coping Skills",
                  description: "Teaches families skills to deal with the impact of trauma and become stronger and more resilient in the healing process.",
                  icon: (
                    <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  )
                },
                {
                  title: "Family Empowerment",
                  description: "Helps families find their voice and advocate for themselves. Most families reported that they were significantly better at speaking up for themselves.",
                  icon: (
                    <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                  )
                },
                {
                  title: "Community Connection",
                  description: "Helps families feel connected and less alone. It helps build trust and assists them in creating supportive communities.",
                  icon: (
                    <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  )
                },
                {
                  title: "Better Quality of Life",
                  description: "Helps improve the quality of life for the family by addressing the impact of trauma.",
                  icon: (
                    <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  )
                }
              ].map((benefit, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">{benefit.title}</h4>
                      <p className="text-sm text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

        {/* Services Section with Full-Width Background */}
        <div id="types" className="py-16 bg-[#00A785]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Our Services</h2>
            
            <div>
              <h3 className="text-lg font-normal text-white mb-6">Comprehensive trauma support services for children and families</h3>
              
              {/* Services Grid - Single Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
                {[
                    {
                      title: "Eye Movement Desensitisation and Reprocessing (EMDR) Therapy",
                      description: "Gentle eye movements that reduce painful feelings associated with memories of trauma.",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )
                    },
                    {
                      title: "Trauma-Focused CBT",
                      description: "An effective therapy that helps children and families overcome trauma to learning and support.",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      )
                    },
                    {
                      title: "Play Therapy",
                      description: "Helps children overcome trauma through play, allowing children to express feelings naturally and comfortably.",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      title: "Trauma Systems Therapy",
                      description: "A therapy that helps children and their environment overcome trauma.",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      )
                    }
                  ].map((type, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {type.icon}
                        </div>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 mb-2">{type.title}</h4>
                          <p className="text-sm text-gray-600">{type.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        </section>
    </div>
  );
};

export default TraumaOutreach;
